import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import proService, { ProEvent, ProParticipant } from '../../services/proService';
import PlayerAvatar from '../spiele/PlayerAvatar';
import PlayerLink from '../PlayerLink';

const ACCENT = '#3fced1';

/** Runde Icon-Scroll-Buttons: erscheinen beim Hover über die Leiste. */
const scrollButtonClass =
  'absolute top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900 text-gray-400 opacity-0 shadow-lg transition-opacity hover:border-[#3fced1]/50 hover:text-white group-hover:opacity-100 sm:flex';

/**
 * Schmale Live-Ticker-Leiste ganz oben (Kicker/OneFootball-Stil):
 * kompakte Score-Chips für laufende & heutige PDC-/Pro-Spiele mit kleinen
 * Spieler-Avataren (Initialen-Fallback). Horizontal scrollbar; beim Hover
 * erscheinen runde Scroll-Buttons (Muster wie NeuBeiDartCloudSection).
 * Blendet sich komplett aus, wenn nichts läuft/ansteht. Community-Live-Turniere
 * laufen NICHT hier, sondern in der Sidebar (SidebarLiveTournaments).
 *
 * CLS-Fix (2026-09-01): solange die Events noch LADEN, reserviert eine
 * Skeleton-Leiste in Chip-Endhöhe den Platz — erst wenn feststeht, dass
 * heute nichts läuft/ansteht, kollabiert die Leiste auf null.
 */
const LiveTickerBar: React.FC<{ events: ProEvent[]; loading?: boolean }> = ({
  events,
  loading = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const liveEvents = events
    .filter((e) => proService.isLive(e))
    .sort((a, b) => proService.compareTickerPriority(a, b));
  // Anstehende Spiele (nächstes zuerst) vor beendeten (zuletzt beendetes
  // zuerst), PDC vor Super Series/ADC — wie im App-Ticker.
  const todayEvents = proService.sortForTicker(proService.filterAndSortByDay(events, 0));
  const tickerEvents = [...liveEvents, ...todayEvents.slice(0, 14 - liveEvents.length)].slice(0, 14);

  // Scroll-Buttons nur anbieten, wenn die Chips tatsächlich überlaufen
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setHasOverflow(el.scrollWidth > el.clientWidth + 4);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [tickerEvents.length]);

  // Skeleton in exakter Chip-Höhe (79px = py-1.5 + Kopfzeile + 2 Score-Rows
  // + Border), damit der Content darunter beim Laden nicht springt
  if (loading && tickerEvents.length === 0) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-2 sm:px-6" aria-hidden="true">
        <div className="flex items-stretch gap-2 overflow-hidden">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-[79px] min-w-[184px] shrink-0 animate-pulse rounded-xl border border-zinc-800 bg-zinc-900"
            />
          ))}
        </div>
      </div>
    );
  }

  if (tickerEvents.length === 0) return null;

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="group">
      <div className="mx-auto flex max-w-7xl items-stretch gap-2 px-4 py-2 sm:px-6">
        {/* Scroll-Bereich mit Hover-Scroll-Buttons an den Rändern */}
        <div className="relative min-w-0 flex-1">
          <div
            ref={scrollRef}
            className="flex items-stretch gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {tickerEvents.map((event, idx) => (
              <EventChip key={proService.getEventId(event) || `evt-${idx}`} event={event} />
            ))}
          </div>

          {hasOverflow && (
            <>
              <button
                type="button"
                aria-label="Zurück scrollen"
                onClick={() => scrollBy(-360)}
                className={`${scrollButtonClass} left-1`}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                aria-label="Weiter scrollen"
                onClick={() => scrollBy(360)}
                className={`${scrollButtonClass} right-1`}
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const chipClass =
  'flex min-w-[184px] shrink-0 flex-col justify-center rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-1.5 transition-colors hover:border-[#3fced1]/50';

const ScoreRow: React.FC<{
  participant?: ProParticipant;
  score: string;
  won: boolean;
  showScore: boolean;
}> = ({ participant, score, won, showScore }) => (
  <div className="flex items-center gap-1.5 py-0.5 text-xs leading-5">
    <PlayerAvatar participant={participant} size="xs" />
    <span
      className={`min-w-0 flex-1 truncate ${won ? 'font-bold' : 'font-medium text-white'}`}
      style={won ? { color: ACCENT } : undefined}
    >
      <PlayerLink name={proService.getParticipantName(participant)} />
    </span>
    {showScore && (
      <span
        className={`shrink-0 font-bold tabular-nums ${won ? '' : 'text-gray-400'}`}
        style={won ? { color: ACCENT } : undefined}
      >
        {score}
      </span>
    )}
  </div>
);

const EventChip: React.FC<{ event: ProEvent }> = ({ event }) => {
  const id = proService.getEventId(event);
  const isLive = proService.isLive(event);
  const finished = proService.isFinished(event);
  const scores = proService.formatScore(event);
  const showScore = isLive || finished;
  const time = proService.formatEventTime(event.startTime);
  const timeSuffix = time !== '-' ? ` · ${time}` : '';
  // Anstehende Spiele: statt des Scores rechts den Modus zeigen (wie App)
  const bestOf = event.matchFormat?.bestOf;
  const modeLabel = !showScore && typeof bestOf === 'number' ? `B.o. ${bestOf}` : null;

  const body = (
    <>
      <div className="mb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide">
        {isLive ? (
          <>
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
            <span className="shrink-0 whitespace-nowrap">
              <span className="text-red-500">Live</span>
              {timeSuffix && (
                <span className="tabular-nums text-gray-500">{timeSuffix}</span>
              )}
            </span>
          </>
        ) : finished ? (
          <span className="shrink-0 whitespace-nowrap tabular-nums text-gray-500">
            Beendet{timeSuffix}
          </span>
        ) : (
          <span className="text-gray-500">{time}</span>
        )}
        {event.tournament?.name && (
          <span className="max-w-[110px] truncate font-medium normal-case tracking-normal text-gray-500">
            {event.tournament.name}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <div className="min-w-0 flex-1">
          <ScoreRow
            participant={event.homeParticipant}
            score={scores.home}
            won={event.winner === 1}
            showScore={showScore}
          />
          <ScoreRow
            participant={event.awayParticipant}
            score={scores.away}
            won={event.winner === 2}
            showScore={showScore}
          />
        </div>
        {modeLabel && (
          <span className="shrink-0 text-[10px] font-semibold text-gray-500">
            {modeLabel}
          </span>
        )}
      </div>
    </>
  );

  if (!id) return <div className={chipClass}>{body}</div>;
  return (
    <Link to={`/spiel/${id}`} className={chipClass} aria-label="Zum Spiel">
      {body}
    </Link>
  );
};

export default LiveTickerBar;
